// client/src/components/clientes/ClienteRow.js - VERSÃO SIMPLIFICADA COM CORES E CORREÇÃO DE AVATAR
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    MoreVertical,
    Phone
} from 'lucide-react';

// ✅ Criar um PlaceholderGenerator simples caso não exista
const PlaceholderGenerator = {
    isPlaceholderUrl: (url) => {
        return !url || url.includes('placehold.co') || url.includes('data:image/svg');
    },
    getAvatarPlaceholder: (name, email, size = 40) => {
        const initial = name ? name.charAt(0).toUpperCase() : 'C';
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Crect width='${size}' height='${size}' fill='%233B82F6' /%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23FFFFFF' font-size='${size/2}' font-family='Arial, sans-serif'%3E${initial}%3C/text%3E%3C/svg%3E`;
    }
};

const ClienteRow = ({
    cliente,
    onOpenActions,
    loading = false,
    selected = false,
    className = '',
    ...props
}) => {
    const [imageError, setImageError] = useState(false);
    
    // Função para formatar telefone
    const formatPhone = (phone) => {
        if (!phone) return '-';
        const cleaned = String(phone).replace(/\D/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (cleaned.length === 10) {
            return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return phone;
    };

    // Função para gerar avatar placeholder
    const renderAvatar = (userData) => {
        const photoUrl = userData.photo || userData.photoUrl;
        const name = userData.name || 'Cliente sem nome';
        const email = userData.email || '';
        
        // Se tem foto e não deu erro, mostra a foto
        if (photoUrl && !imageError && !PlaceholderGenerator.isPlaceholderUrl(photoUrl)) {
            return (
                <div className="relative">
                    <img 
                        src={photoUrl} 
                        alt={name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                        onError={() => setImageError(true)}
                        loading="lazy"
                    />
                    {userData.isActive && (
                        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                    )}
                </div>
            );
        }

        // Se não tem foto ou deu erro, usa o gerador SVG local
        const avatarSvg = PlaceholderGenerator.getAvatarPlaceholder(name, email, 40);

        return (
            <div className="relative">
                <img 
                    src={avatarSvg}
                    alt={name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                />
                 {userData.isActive && (
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                )}
            </div>
        );
    };

    // Loading state
    if (!cliente?.user || loading) {
        return (
            <tr className="cliente-row cliente-row-loading">
                <td className="px-4 py-3">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="ml-3 flex-1">
                            <div className="h-4 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
                            <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                        </div>
                    </div>
                </td>
                <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                </td>
                <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                        <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                </td>
            </tr>
        );
    }

    const { user } = cliente;
    const userData = user || {};
    const nomeParaExibir = userData.name || 'Cliente sem nome';
    
    // Status simplificado
    const getStatusInfo = () => {
        if (!userData.isActive) {
            return { label: 'Inativo', variant: 'inactive' };
        }
        return { label: 'Ativo', variant: 'active' };
    };

    const status = getStatusInfo();

    return (
        <tr
            className={`cliente-row ${selected ? 'cliente-row-selected' : ''} ${className}`}
            onClick={() => onOpenActions(cliente)}
            {...props}
        >
            {/* Coluna Cliente */}
            <td className="cliente-cell-main">
                <div className="cliente-info">
                    {renderAvatar(userData)}
                    
                    <div className="cliente-details">
                        <div className="cliente-header">
                            <h4 className="cliente-name">
                                {nomeParaExibir}
                            </h4>
                            
                            <span className={`cliente-status cliente-status-${status.variant}`}>
                                {status.label}
                            </span>
                        </div>
                    </div>
                </div>
            </td>

            {/* Coluna Telefone */}
            <td className="cliente-cell-telefone">
                <div className="flex items-center text-gray-700">
                    <Phone size={14} className="mr-2 text-gray-400 flex-shrink-0" />
                    <span className="font-medium">
                        {formatPhone(userData.telefone)}
                    </span>
                </div>
            </td>

            {/* Coluna Ações */}
            <td className="text-right">
                <button
                    onClick={(e) => { e.stopPropagation(); onOpenActions(cliente); }}
                    disabled={loading}
                    className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Ações"
                >
                    <MoreVertical size={18} />
                </button>
            </td>
        </tr>
    );
};

ClienteRow.propTypes = {
    cliente: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        user: PropTypes.shape({
            _id: PropTypes.string,
            name: PropTypes.string,
            telefone: PropTypes.string,
            photo: PropTypes.string,
            photoUrl: PropTypes.string,
            isActive: PropTypes.bool,
            email: PropTypes.string,
        }).isRequired,
    }).isRequired,
    onOpenActions: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    selected: PropTypes.bool,
    className: PropTypes.string,
};

export default React.memo(ClienteRow);